const identity = (x) => x;

/**
 * @typedef {object} ElemInfo
 * @property {string} id            id
 * @property {string} type          type
 * @property {object} props         props
 * @property {import('vue/types/options').AsyncComponentFactory} component     component def
 * @property {ElemInfo[]} children  children
 * @return {import("vue/types/umd").VNode[]}
 */
/**
 * Recursively renders elems' collection
 * @param {import('vue').CreateElement} h                   createElement function provided by vue
 * @param {ElemInfo} elemInfo                               elem info
 * @param {import('vue').VNodeData|((ei:ElemInfo) => import('vue').VNodeData)} [vnodeData={}]          vnode data addon options
 * @param {boolean} [isEditorMode=false]                    editor mode
 * @param {object} [slotData={}]                            slot data object (holds the scoped slot context)
 * @return {import("vue/types/umd").VNode}
 */

const createRenderFactory = (h, elemInfo, vnodeData = {}, isEditorMode = false, slotData = {}) => {
    // we are mapping our nodes array to a hash keyed by 'slot'
    /** @type {Object.<string, ElemInfo[]>} */
    const slots = elemInfo.children.reduce((slotsAcc, child) => {
        const slotName = child.props.slot || 'default';
        // eslint-disable-next-line
        slotsAcc[slotName] = slotsAcc[slotName] ?? [];
        slotsAcc[slotName].push(child);

        return slotsAcc;
    }, {});
    // next we need to transform our hash values a factory methods
    /** @type {Record<string, function (props: object): import("vue/types/vue").VNode[]>} */
    const scopedSlots = Object.entries(slots).reduce((accSlots, [slotName, slotElemInfos]) => {
        const slotElemRenderFns = slotElemInfos.map((slotElemInfo) =>
            createRenderFactory(h, slotElemInfo, vnodeData, isEditorMode)
        );

        const scopedSlot = (props) => {
            const buildData = (data) => ({
                ...data,
                props: {
                    ...data.props,
                    slotData: {
                        ...data.props.slotData,
                        ...props
                    }
                }
            });

            return slotElemRenderFns.map((render) => render({ buildData }));
        };

        return {
            ...accSlots,
            [slotName]: scopedSlot
        };
    }, {});

    const { id, type, props } = elemInfo;
    const dataAddon = typeof vnodeData === 'function' ? vnodeData(elemInfo) : vnodeData;
    const data = {
        props: { id, type, initProps: props, slotData, isEditorMode },
        scopedSlots,
        key: elemInfo.id,
        ...dataAddon
    };

    // create vnode
    return ({ buildData = identity } = {}) => h(elemInfo.component, buildData(data));
};

const render = (...args) => createRenderFactory(...args).call();

/**
 * @param {import('vue').CreateElement} h
 * @param {ElemInfo[]} slotElemInfos
 * @param {import('vue').VNodeData|((ei:ElemInfo) => import('vue').VNodeData)} vnodeData
 * @param {boolean} isEditorMode
 * @returns {(props:Record<string, any>) => import("vue/types/umd").VNode[]}
 */
const createSlotFactory = (h, slotElemInfos, vnodeData, isEditorMode) => {
    // anonymous function definition is jit stable here.
    return (props) => slotElemInfos.map((slotElemInfo) => renderTree(h, slotElemInfo, vnodeData, isEditorMode, props));
};

/**
 * @param {import('vue').CreateElement} h
 * @param {Record<string, ElemInfo[]>} slots
 * @param {import('vue').VNodeData|((ei:ElemInfo) => import('vue').VNodeData)} vnodeData
 * @param {boolean} isEditorMode
 * @returns {Record<string, Function>}
 */
const createScopedSlots = (h, slots, vnodeData, isEditorMode) =>
    Object.entries(slots).reduce((slotsAcc, [slotName, slotElemInfos]) => {
        // call the stable factory creator for each slot; jit interprets this is an execution, not a definition.
        slotsAcc[slotName] = createSlotFactory(h, slotElemInfos, vnodeData, isEditorMode);
        return slotsAcc;
    }, {});

/**
 * Recursively renders elems' collection
 * @param {import('vue').CreateElement} h                   createElement function provided by vue
 * @param {ElemInfo} elemInfo                               elem info
 * @param {import('vue').VNodeData|((ei:ElemInfo) => import('vue').VNodeData)} [vnodeData={}]          vnode data addon options
 * @param {boolean} [isEditorMode=false]                    editor mode
 * @param {object} [slotData={}]                            slot data object (holds the scoped slot context)
 * @return {import("vue/types/umd").VNode}
 */
const renderTree = (h, elemInfo, vnodeData = {}, isEditorMode = false, slotData = {}) => {
    // we are mapping our nodes array to a hash keyed by 'slot'
    /** @type {Record<string, ElemInfo[]>} */
    const slots = elemInfo.children.reduce((slotsAcc, child) => {
        const slotName = child.props.slot || 'default';
        // eslint-disable-next-line
        slotsAcc[slotName] = slotsAcc[slotName] ?? [];
        slotsAcc[slotName].push(child);
        return slotsAcc;
    }, {});

    // next we need to transform our hash values a factory methods
    // The JIT compilation of the inner function is now externalized and stable.
    /** @type {Object.<string, (props:object) => import("vue/types/umd").VNode[]>} */
    const scopedSlots = createScopedSlots(h, slots, vnodeData, isEditorMode);

    const { id, type, props: initProps, component } = elemInfo;
    const dataAddon = typeof vnodeData === 'function' ? vnodeData(elemInfo) : vnodeData;

    // create vnode
    return h(component, {
        props: { id, type, initProps, slotData, isEditorMode },
        scopedSlots,
        key: id,
        ...dataAddon
    });
};

export { createRenderFactory, render, renderTree };
